'use client';

import { useLocale, useTranslations } from 'next-intl';
import { usePathname, useRouter } from 'next/navigation';
import { Globe } from 'lucide-react';
import { Button } from '@/src/shared/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/src/shared/ui/dropdown-menu';
import { i18nRouting } from '@/src/shared/config';
import { withLocale } from '@/src/shared/lib/i18n';
import { cn } from '@/src/shared/lib/shadcn';

type SupportedLocale = (typeof i18nRouting.locales)[number];

type Props = {
  className?: string;
};

export function LocaleSwitcher({ className }: Props) {
  const locale = useLocale() as SupportedLocale;
  const t = useTranslations('nav.locale');
  const pathname = usePathname();
  const router = useRouter();

  const localeLabel: Record<SupportedLocale, string> = {
    es: t('es'),
    en: t('en'),
  };

  function switchTo(next: SupportedLocale) {
    if (next === locale) return;
    router.push(withLocale(pathname, next));
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          aria-label={t('label')}
          className={cn('h-11 gap-1.5 px-2 sm:h-9', className)}
        >
          <Globe className="size-3.5 shrink-0" />
          <span className="text-xs font-medium uppercase">{locale}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-32">
        {i18nRouting.locales.map((loc) => (
          <DropdownMenuItem
            key={loc}
            onClick={() => switchTo(loc as SupportedLocale)}
            className={locale === loc ? 'font-semibold' : ''}
          >
            {localeLabel[loc as SupportedLocale]}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
